import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Repeat, HelpOutline } from '@mui/icons-material';
import moment from 'moment';

interface CreateScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onCreateSchedule: (name: string, frequency: string, startDate: string) => void;
  onOpenHelp?: (section?: string) => void;
}

type Frequency = 'none' | 'weekly' | 'bi-weekly' | 'monthly';

const FREQUENCY_OPTIONS: { value: Frequency; label: string; description: string; color: 'default' | 'primary' | 'success' | 'warning' }[] = [
  { value: 'none', label: 'None', description: 'Single schedule, no repeat', color: 'default' },
  { value: 'weekly', label: 'Weekly', description: 'Repeats every 7 days', color: 'primary' },
  { value: 'bi-weekly', label: 'Bi-weekly', description: 'Repeats every 14 days', color: 'success' },
  { value: 'monthly', label: 'Monthly', description: 'Repeats every month', color: 'warning' },
];

const getScheduleEnd = (start: moment.Moment, frequency: Frequency): moment.Moment => {
  switch (frequency) {
    case 'none': return start.clone();
    case 'weekly': return start.clone().add(6, 'days');
    case 'bi-weekly': return start.clone().add(13, 'days');
    case 'monthly': return start.clone().add(1, 'month').subtract(1, 'day');
  }
};

const getSchedulePeriods = (start: moment.Moment, frequency: Frequency, count: number) => {
  const periods: { start: moment.Moment; end: moment.Moment }[] = [];
  let current = start.clone();
  for (let i = 0; i < count; i++) {
    const end = getScheduleEnd(current, frequency);
    periods.push({ start: current.clone(), end: end.clone() });
    current = end.clone().add(1, 'day');
  }
  return periods;
};

const PERIOD_COLORS = [
  { bg: 'primary.light', bgLight: 'rgba(25, 118, 210, 0.10)', text: 'primary.dark' },
  { bg: 'success.light', bgLight: 'rgba(46, 125, 50, 0.10)', text: 'success.dark' },
  { bg: 'warning.light', bgLight: 'rgba(237, 108, 2, 0.10)', text: 'warning.dark' },
  { bg: 'info.light', bgLight: 'rgba(2, 136, 209, 0.10)', text: 'info.dark' },
];

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({ open, onClose, onCreateSchedule, onOpenHelp }) => {
  const [scheduleName, setScheduleName] = useState('');
  const [selectedStart, setSelectedStart] = useState<moment.Moment | null>(null);
  const [frequency, setFrequency] = useState<Frequency>('none');
  const [calendarMonth, setCalendarMonth] = useState(moment().startOf('month'));

  const handleClose = () => {
    setScheduleName('');
    setSelectedStart(null);
    setFrequency('none');
    setCalendarMonth(moment().startOf('month'));
    onClose();
  };

  const handleCreate = () => {
    if (!scheduleName.trim()) return;
    if (frequency === 'none') {
      onCreateSchedule(scheduleName.trim(), frequency, '');
      handleClose();
    } else if (selectedStart) {
      onCreateSchedule(scheduleName.trim(), frequency, selectedStart.format('YYYY-MM-DD'));
      handleClose();
    }
  };

  const handleDayClick = (cellDate: moment.Moment) => {
    if (selectedStart?.isSame(cellDate, 'day')) {
      setSelectedStart(null);
    } else {
      setSelectedStart(cellDate.clone());
    }
  };

  const periods = useMemo(() => {
    if (!selectedStart || frequency === 'none') return [];
    return getSchedulePeriods(selectedStart, frequency, 4);
  }, [selectedStart, frequency]);

  const firstDay = calendarMonth.clone().startOf('month');
  const daysInMonth = firstDay.daysInMonth();
  const startOffset = firstDay.day();
  const totalCells = Math.ceil((daysInMonth + startOffset) / 7) * 7;

  const firstPeriod = periods[0];
  const freqOption = FREQUENCY_OPTIONS.find(f => f.value === frequency)!;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create New Schedule</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Schedule Name"
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
          placeholder="e.g., Summer 2026, Weekend Shifts"
          sx={{ mt: 1, mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={frequency}
            label="Frequency"
            onChange={(e) => setFrequency(e.target.value as Frequency)}
          >
            {FREQUENCY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Repeat sx={{ fontSize: 18, color: `${opt.color}.main` }} />
                  <Typography variant="body2">{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">— {opt.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Typography
            variant="caption"
            color="primary"
            onClick={() => onOpenHelp?.('schedules')}
            sx={{ mt: 0.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}
          >
            <HelpOutline sx={{ fontSize: 14 }} />
            What does frequency mean?
          </Typography>
        </FormControl>

        {frequency !== 'none' && (<>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {!selectedStart
            ? 'Click a day to set the start date'
            : 'Click a different day to change, or click again to deselect'}
        </Typography>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          {/* Month navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" onClick={() => setCalendarMonth(m => m.clone().subtract(1, 'month'))}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              {calendarMonth.format('MMMM YYYY')}
            </Typography>
            <IconButton size="small" onClick={() => setCalendarMonth(m => m.clone().add(1, 'month'))}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Weekday headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
            {WEEK_DAYS.map(d => (
              <Typography key={d} variant="caption" color="text.secondary" fontWeight={700} sx={{ textAlign: 'center' }}>
                {d}
              </Typography>
            ))}
          </Box>

          {/* Day cells */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {Array.from({ length: totalCells }, (_, i) => {
              const dayNum = i - startOffset + 1;
              const isCurrentMonth = dayNum > 0 && dayNum <= daysInMonth;
              if (!isCurrentMonth) return <Box key={i} sx={{ height: 34 }} />;

              const cellDate = firstDay.clone().date(dayNum);
              const isToday = cellDate.isSame(moment(), 'day');
              const isStart = !!selectedStart?.isSame(cellDate, 'day');

              // Determine which period this date belongs to
              let periodIndex = -1;
              let isPeriodStart = false;
              let isPeriodEnd = false;
              for (let p = 0; p < periods.length; p++) {
                if (cellDate.isBetween(periods[p].start, periods[p].end, 'day', '[]')) {
                  periodIndex = p;
                  isPeriodStart = cellDate.isSame(periods[p].start, 'day');
                  isPeriodEnd = cellDate.isSame(periods[p].end, 'day');
                  break;
                }
              }

              const inPeriod = periodIndex >= 0;
              const colorSet = inPeriod ? PERIOD_COLORS[periodIndex % PERIOD_COLORS.length] : null;
              const isEndCap = isPeriodStart || isPeriodEnd;

              return (
                <Box
                  key={i}
                  onClick={() => handleDayClick(cellDate)}
                  sx={{
                    height: 34,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: isToday || isEndCap ? '50%' : '4px',
                    bgcolor: isToday
                      ? 'error.light'
                      : isEndCap && colorSet
                      ? colorSet.bg
                      : inPeriod && colorSet
                      ? colorSet.bgLight
                      : 'transparent',
                    color: isToday
                      ? 'white'
                      : isEndCap && colorSet
                      ? colorSet.text
                      : 'text.primary',
                    fontWeight: isStart || isToday ? 700 : 400,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    position: 'relative',
                    outline: isToday ? '2px solid' : 'none',
                    opacity: isToday && inPeriod && !isEndCap ? 0.5 : 1,
                    outlineColor: isToday ? 'error.main' : 'transparent',
                    outlineOffset: '1px',
                    userSelect: 'none',
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      bgcolor: isToday ? 'error.main' : isEndCap && colorSet
                        ? `${colorSet.bg}`
                        : inPeriod
                        ? colorSet?.bg
                        : 'action.hover',
                      opacity: isEndCap ? 0.85 : 1,
                    },
                  }}
                >
                  {dayNum}
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* Today label */}
        <Box
          onClick={() => {
            const today = moment();
            setSelectedStart(today.clone().startOf('day'));
            setCalendarMonth(today.clone().startOf('month'));
          }}
          sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
        >
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main',  flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            Today — {moment().format('MMM D, YYYY')}
          </Typography>
        </Box>

        {/* Legend */}
        {selectedStart && periods.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {periods.map((period, idx) => {
              const color = PERIOD_COLORS[idx % PERIOD_COLORS.length];
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color.bg, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">
                    {idx === 0 ? 'Schedule' : `Repeat ${idx}`}: {period.start.format('MMM D')} – {period.end.format('MMM D, YYYY')}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Frequency summary */}
        <Box sx={{ mt: 1.5, minHeight: 36 }}>
          {selectedStart && firstPeriod ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Repeat fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Starts {firstPeriod.start.format('MMM D, YYYY')}
                {' · '}
                {firstPeriod.end.diff(firstPeriod.start, 'days') + 1} days per period
              </Typography>
              <Chip
                icon={<Repeat sx={{ fontSize: '0.85rem !important' }} />}
                label={freqOption.label}
                size="small"
                color={freqOption.color}
                variant="outlined"
              />
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">
              Select a start date on the calendar above
            </Typography>
          )}
        </Box>
        </>)}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!scheduleName.trim() || (frequency !== 'none' && !selectedStart)}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateScheduleModal;

